// Unpublished Work (c) 2023-2024 Deere & Company.
import { Alert } from "@deere/fuel-react";
import { InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { RBACRole } from "../../../../api/rbac/types";
import { useConfigContext } from "../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../context/DealerAccountContext";
import { useFeatureToggleContextV2 } from "../../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { PreferenceContext } from "../../../../context/PreferencesContext/PreferencesContext";
import { RBACAccessContext } from "../../../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../../../context/UserContext/UserContext";
import { CATEGORIES, TOGGLES } from "../../../featureToggles/constants";
import { Preference } from "../../../preferences/types";
import { Order, OrderStatus, orderTypeToUrlParamMapping } from "../../types";
import {
  canDisplayOrderCurrency,
  computeOrderPriceAsString,
  getEuDealerPriceFlags,
  getOrderCurrencyCode,
  getOrderCurrencySymbol,
  isCodedPricePreferred,
} from "../../utils";

const isJDPSToggleEnabled = (
  isToggleEnabled: (featureName: string, toggleName: string) => boolean,
  enableFeatureToggle: boolean
) =>
  enableFeatureToggle &&
  isToggleEnabled(CATEGORIES.EXTREME_UI, TOGGLES.ENABLE_MARKUP_PRICE);

export function maskPriceValue(
  userPreferences: Preference[],
  isToggleEnabled: (featureName: string, toggleName: string) => boolean,
  isFeatureToggleContextV2Enabled: boolean
): boolean {
  return (
    isCodedPricePreferred(userPreferences) ||
    isJDPSToggleEnabled(isToggleEnabled, isFeatureToggleContextV2Enabled)
  );
}

const EstimatedCost = ({
  order,
  dealerAccountId,
}: {
  order: Order;
  dealerAccountId?: string;
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["orders"]);
  const { dealerAccount, dealerLocation, mappedAccounts } =
    useContext(DealerAccountContext);
  const {
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_EU_DEALER_LEGACY_PRICE,
    ENABLE_USE_ORDER_RESPONSE_CURRENCY_CODE = false,
  } = useConfigContext();
  const { userPreferences } = useContext(PreferenceContext);
  const { isToggleEnabled } = useFeatureToggleContextV2();
  const user = useContext(UserContext);
  const { isRBACEnabled } = useContext(RBACAccessContext);
  const maskPrice = maskPriceValue(
    userPreferences,
    isToggleEnabled,
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2
  );
  const {
    euDealerCG5: isEuDealerCG5,
    euDealerPriceMasked: isEuDealerPriceMasked,
  } = getEuDealerPriceFlags(
    dealerAccount?.salesDetails?.customerGroup5,
    ENABLE_EU_DEALER_LEGACY_PRICE
  );
  const shouldMaskPrice = maskPrice || isEuDealerPriceMasked;
  const costHeaderSx = {
    fontWeight: theme.tokens.FuelFontWeightSemibold,
    fontSize: theme.tokens.FuelFontSizeHeading4,
    color: theme.tokens.FuelColorTextPrimary,
  };
  const costBreakDownHeaderSx = {
    ...costHeaderSx,
    fontWeight: theme.tokens.FuelFontWeightSemibold,
    paddingBottom: theme.tokens.FuelSpacing4,
  };

  const costBreakTotalHeaderSx = {
    ...costHeaderSx,
    fontWeight: theme.tokens.FuelFontWeightRegular,
    paddingBottom: theme.tokens.FuelSpacing4,
  };

  const breakdownAlertSx = {
    paddingBottom: theme.tokens.FuelSpacing4,
  };
  const costBreakDownValueSx = {
    ...costHeaderSx,
    fontSize: theme.tokens.FuelFontSizeSm,
    paddingBottom: theme.tokens.FuelSpacing4,
    float: "right",
  };
  const costBreakDownTotalValueSx = {
    ...costBreakDownValueSx,
    fontWeight: theme.tokens.FuelFontWeightRegular,
    fontSize: theme.tokens.FuelFontSizeHeading4,
  };

  const location =
    isRBACEnabled && user?.is(RBACRole.CORPORATE_PARTS_MANAGER)
      ? mappedAccounts.find(
          (mappedAccount) => mappedAccount.id === dealerAccountId
        )?.location.country
      : dealerLocation;
  const accountNumber = order.accountId;
  const estimatedCostSummaryUrl = `/orders/${
    orderTypeToUrlParamMapping[order.orderType]
  }/${order.orderNumber}/estimated-cost-summary?accountNumber=${accountNumber}`;
  return (
    <Box width="100%" data-testid="order-estimated-cost-summary">
      <Card
        sx={{
          boxShadow: "none",
        }}
      >
        <CardContent
          style={{
            minHeight: "200px",
            display: "flex",
            flexDirection: "column",
            padding: theme.tokens.FuelSpacing4,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Grid container gap={theme.tokens.FuelSpacing2}>
              <Grid>
                <Typography
                  sx={costBreakDownHeaderSx}
                  data-testid="estimated-cost-of-order-header"
                >
                  {t("orderEstimatedCostSummary.title")}
                  {canDisplayOrderCurrency(
                    order.currencyCode,
                    location,
                    ENABLE_USE_ORDER_RESPONSE_CURRENCY_CODE
                  ) &&
                    !isEuDealerPriceMasked && (
                      <span>
                        (
                        {getOrderCurrencyCode(
                          order.currencyCode,
                          location,
                          dealerAccount?.mruSalesOrg,
                          ENABLE_USE_ORDER_RESPONSE_CURRENCY_CODE
                        )}
                        )
                      </span>
                    )}
                </Typography>
              </Grid>
              <Grid>
                <Tooltip
                  title={t("orderEstimatedCostSummary.titleInfoText")}
                  data-testid="estimated-cost-of-order-header-tooltip"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: "none",
                      },
                    },
                  }}
                  placement="top-end"
                >
                  <IconButton sx={{ paddingBottom: theme.tokens.FuelSpacing4 }}>
                    <InfoOutlined
                      sx={{ fill: theme.tokens.FuelColorAlertInfoIcon }}
                    />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>

            <Typography sx={costBreakTotalHeaderSx} data-testid="total-header">
              {t("orderEstimatedCostSummary.total")}
              <Typography
                display="inline"
                component="span"
                sx={costBreakDownTotalValueSx}
              >
                {!shouldMaskPrice &&
                  canDisplayOrderCurrency(
                    order.currencyCode,
                    location,
                    ENABLE_USE_ORDER_RESPONSE_CURRENCY_CODE
                  ) && (
                    <Typography
                      component="span"
                      data-testid="total-value-currency"
                    >
                      {getOrderCurrencySymbol(
                        order.currencyCode,
                        location,
                        dealerAccount?.mruSalesOrg,
                        ENABLE_USE_ORDER_RESPONSE_CURRENCY_CODE
                      )}{" "}
                    </Typography>
                  )}
                <Typography data-testid="total-value" component="span">
                  {computeOrderPriceAsString(order, shouldMaskPrice)}
                </Typography>
              </Typography>
            </Typography>
          </Box>
          <Box>
            {!(
              order.orderStatus === OrderStatus.CANCELLED ||
              isJDPSToggleEnabled(
                isToggleEnabled,
                ENABLE_FEATURE_TOGGLE_CONTEXT_V2
              ) ||
              isEuDealerCG5
            ) && (
              <Alert
                variant="info"
                sx={breakdownAlertSx}
                data-testid="detailed-breakdown-alert-info"
                data-html2canvas-ignore="true"
              >
                <Typography
                  data-testid="detailed-breakdown-link"
                  component="span"
                >
                  {t("orderEstimatedCostSummary.breakdownInfoHeader")}
                  <Link
                    to={estimatedCostSummaryUrl}
                    target="_blank"
                    style={{
                      color: theme.tokens.FuelColorBrandPrimary,
                    }}
                  >
                    {t("orderEstimatedCostSummary.breakdownInfoLink")}
                  </Link>
                  {t("orderEstimatedCostSummary.breakdownInfoText")}
                </Typography>
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EstimatedCost;
